/**
 * Add Data Source — thin orchestrator.
 * 1. Connector selection (from API)
 * 2. Where to place (parent folder)
 * 3. OAuth redirect for oauth2 connectors, or show connector setup for API-key connectors
 * 4. After OAuth callback: render per-connector setup (GoogleDriveSetup, GscSetup, etc.) via registry
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Database } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { apiService } from '../../services/api';
import { getConnectors, ConnectorMeta } from '../index';
import { Folder as FolderType } from '../../types';
import ConnectorLogo, { hasConnectorLogo } from '../../components/ConnectorLogo';
import { RenderConnectorSetup, hasSetup } from '../setupRegistry';

type Step = 'connector' | 'parent_folder';

const DataSourceFlow: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const credentialId = searchParams.get('credential_id');
  const connectorTypeFromUrl = searchParams.get('connector_type');
  const parentFolderIdFromUrl = searchParams.get('parent_folder_id');
  const asKnowledgeBaseFromUrl = searchParams.get('as_knowledge_base') === 'true';

  const [step, setStep] = useState<Step>('connector');
  const [connectors, setConnectors] = useState<ConnectorMeta[]>([]);
  const [selectedConnector, setSelectedConnector] = useState<ConnectorMeta | null>(null);
  const [parentFolderId, setParentFolderId] = useState<string | null>(null);
  const [asKnowledgeBase, setAsKnowledgeBase] = useState(false);
  const [availableFolders, setAvailableFolders] = useState<FolderType[]>([]);
  const hasShownConnectedToast = useRef(false);

  useEffect(() => {
    getConnectors().then(setConnectors).catch(() => setConnectors([]));
  }, []);

  useEffect(() => {
    if (step === 'parent_folder') {
      apiService.getFolders(true, 'user').then(setAvailableFolders).catch(() => setAvailableFolders([]));
    }
  }, [step]);

  // Toast on OAuth return (once per mount so it doesn't show twice in Strict Mode or on re-renders)
  useEffect(() => {
    if (!credentialId || !connectorTypeFromUrl || searchParams.get('status') !== 'connected') return;
    if (hasShownConnectedToast.current) return;
    hasShownConnectedToast.current = true;
    toast.success(
      connectorTypeFromUrl === 'google_search_console'
        ? 'Google Search Console connected! Select a property.'
        : 'Connected! Complete setup below.'
    );
  }, [credentialId, connectorTypeFromUrl, searchParams]);

  // Post-OAuth: render connector-specific setup (Drive folder picker, GSC property picker, etc.)
  if (credentialId && connectorTypeFromUrl && hasSetup(connectorTypeFromUrl)) {
    return (
      <RenderConnectorSetup
        credentialId={credentialId}
        parentFolderId={asKnowledgeBaseFromUrl ? null : (parentFolderIdFromUrl || parentFolderId)}
        asKnowledgeBase={asKnowledgeBaseFromUrl}
        connectorType={connectorTypeFromUrl}
        onCreated={dataSourceId => navigate(`/data-sources/${dataSourceId}`)}
        onBack={() => navigate('/data-sources')}
      />
    );
  }

  const handleSelectConnector = (c: ConnectorMeta) => {
    setSelectedConnector(c);
    if (hasSetup(c.id) || c.auth_type === 'oauth2' || c.auth_type === 'api_key') {
      setStep('parent_folder');
    } else {
      toast.error('This connector is not yet supported.');
    }
  };

  const handleParentFolderNext = async () => {
    if (!selectedConnector) return;
    if (selectedConnector.auth_type === 'oauth2') {
      try {
        const authUrl = await apiService.getConnectUrl(
          selectedConnector.id,
          asKnowledgeBase ? undefined : (parentFolderId || undefined),
          asKnowledgeBase,
          typeof window !== 'undefined' ? window.location.origin : undefined
        );
        window.location.href = authUrl;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to start sign-in';
        toast.error(msg);
        console.error('[OAuth] Failed to get connect URL:', {
          connector: selectedConnector.id,
          error: msg,
          hint: 'If this is scope-related, ensure the scope is added in Google Cloud Console → OAuth consent screen → Scopes.',
        });
      }
      return;
    }
    // API-key / other: go to connector setup (no credentialId yet; connector will collect and create)
    if (hasSetup(selectedConnector.id)) {
      // For now we don't have an API-key connector with a setup that creates credential client-side.
      // When we do: setStep('connector_setup') and render setup with credentialId=null.
      toast.error('API key connectors not yet implemented in this flow.');
    }
  };

  // Step: connector selection
  if (step === 'connector') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 py-8">
          <button
            type="button"
            onClick={() => navigate('/data-sources')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">Add Data Source</h1>
          <p className="text-gray-500 mb-6">Select a source to sync data into your knowledge base.</p>
          <div className="grid gap-4 sm:grid-cols-2">
            {connectors.map(c => (
              <button
                key={c.id}
                type="button"
                onClick={() => handleSelectConnector(c)}
                className={`flex items-start gap-4 p-4 rounded-xl border-2 text-left transition-colors ${selectedConnector?.id === c.id ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300 bg-white'}`}
              >
                <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  {hasConnectorLogo(c.id) ? (
                    <ConnectorLogo connectorType={c.id} size={28} alt={c.name} />
                  ) : (
                    <Database className="w-6 h-6 text-blue-600" />
                  )}
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">{c.name}</h3>
                  <p className="text-sm text-gray-500 mt-0.5">{c.description}</p>
                </div>
              </button>
            ))}
          </div>
          {connectors.length === 0 && <p className="text-gray-500">No connectors available.</p>}
        </div>
      </div>
    );
  }

  // Step: parent folder (where to place)
  if (step === 'parent_folder' && selectedConnector) {
    const isOAuth = selectedConnector.auth_type === 'oauth2';
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 py-8">
          <button
            type="button"
            onClick={() => setStep('connector')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          {hasConnectorLogo(selectedConnector.id) && (
            <div className="flex items-center gap-3 mb-4">
              <ConnectorLogo
                connectorType={selectedConnector.id}
                size={36}
                alt={selectedConnector.name}
              />
              <span className="text-gray-600 font-medium">{selectedConnector.name}</span>
            </div>
          )}
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">
            Where should this Data Source appear?
          </h1>
          <p className="text-gray-500 mb-6">Add to your folder tree or as a Knowledge Base (add as context to folders later).</p>
          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
            <label className="flex items-center gap-3 p-3 rounded-lg border border-amber-200 bg-amber-50 cursor-pointer">
              <input
                type="checkbox"
                checked={asKnowledgeBase}
                onChange={e => setAsKnowledgeBase(e.target.checked)}
                className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              <span className="text-sm font-medium text-gray-800">Add as Knowledge Base</span>
              <span className="text-xs text-gray-500">(not in folder tree; can be added as context to any folder)</span>
            </label>
            {!asKnowledgeBase && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                <select
                  value={parentFolderId || ''}
                  onChange={e => setParentFolderId(e.target.value || null)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                >
                  <option value="">Root (top level)</option>
                  {availableFolders.map(f => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <button
              type="button"
              onClick={handleParentFolderNext}
              className="w-full py-3 px-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
            >
              {isOAuth ? 'Continue to sign-in' : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default DataSourceFlow;
